import React, { useEffect, useState } from "react";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { Modal, Field } from "./Fields";
import ReceiptUploader, { ReceiptBadge, ReceiptGallery } from "../components/ReceiptUploader";
import AddableSelect from "../components/AddableSelect";
import { api, fmtUSD, fmtNum, todayISO } from "../lib/api";
import { useYear } from "../context/YearContext";
import { Plus, Trash2, Pencil, ChevronDown, ChevronRight, Cog, Stethoscope, Wheat as WheatIcon, Beef, Bird, Heart, DollarSign, Baby, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const SPECIES = ["Cattle", "Horse", "Chicken", "Sheep", "Goat", "Pig", "Other"];
const STATUS = [
  { value: "active", label: "Active" },
  { value: "sold", label: "Sold" },
  { value: "deceased", label: "Deceased" },
];
const EVENT_TYPES = [
  { value: "vet", label: "Vet visit", icon: Stethoscope },
  { value: "feed", label: "Feed", icon: WheatIcon },
  { value: "vaccination", label: "Vaccination", icon: Heart },
  { value: "breeding", label: "Breeding / AI", icon: Heart },
  { value: "equipment", label: "Equipment", icon: Cog },
  { value: "other", label: "Other", icon: Cog },
];

const speciesIcon = (s) => {
  if (s === "Chicken") return Bird;
  if (s === "Cattle") return Beef;
  return Beef;
};

const blankAnimal = {
  name: "", species: "Cattle", breed: "", kind: "individual", count: 1,
  birth_date: "", purchase_date: todayISO(), purchase_cost: "",
  current_value: "", current_value_as_of: todayISO(),
  status: "active", notes: "", receipts: [],
};

const blankEvent = {
  livestock_id: "", date: todayISO(), event_type: "vet",
  description: "", cost: "", vendor: "", notes: "", receipts: [],
};

const blankSale = {
  livestock_id: "", date: todayISO(), sale_type: "animal", product: "head",
  quantity: "", unit: "head", price_per_unit: "", buyer: "", notes: "", receipts: [],
};

export default function Livestock() {
  const { year, refreshYears } = useYear();
  const [animals, setAnimals] = useState([]);
  const [events, setEvents] = useState([]);
  const [sales, setSales] = useState([]);
  const [breedingStatus, setBreedingStatus] = useState([]);
  const [expanded, setExpanded] = useState({});

  const [aOpen, setAOpen] = useState(false);
  const [aEditing, setAEditing] = useState(null);
  const [aForm, setAForm] = useState(blankAnimal);

  const [eOpen, setEOpen] = useState(false);
  const [eEditing, setEEditing] = useState(null);
  const [eForm, setEForm] = useState(blankEvent);

  const [sOpen, setSOpen] = useState(false);
  const [sForm, setSForm] = useState(blankSale);

  const [galleryReceipts, setGalleryReceipts] = useState(null);

  const [medications, setMedications] = useState([]);
  const [medOpen, setMedOpen] = useState(false);
  const [medEditing, setMedEditing] = useState(null);
  const [medForm, setMedForm] = useState({
    livestock_id: "", medication_name: "", dosage: "", doses_per_day: 1,
    start_date: todayISO(), days: 7, withdrawal_days: 0, cost: "", vendor: "", notes: "", receipts: [],
  });

  const load = async () => {
    const [a, e, s, b, m] = await Promise.all([
      api.get("/livestock"),
      api.get("/care-events"),
      api.get("/livestock-sales"),
      api.get("/livestock/breeding-status"),
      api.get(`/medications?year=${year}`),
    ]);
    setAnimals(a.data);
    setEvents(e.data);
    setSales(s.data);
    setBreedingStatus(b.data);
    setMedications(m.data);
  };

  useEffect(() => { load(); }, [year]);

  const eventsFor = (id) => events.filter((e) => e.livestock_id === id);
  const salesFor = (id) => sales.filter((s) => s.livestock_id === id);
  const breedingFor = (id) => breedingStatus.find((b) => b.livestock_id === id);
  const medsFor = (id) => medications.filter((m) => m.livestock_id === id);
  const careCostFor = (id) => eventsFor(id).reduce((sum, e) => sum + (e.cost || 0), 0);
  const salesRevenueFor = (id) => salesFor(id).reduce((sum, s) => sum + (s.total_revenue || 0), 0);

  // Medication CRUD
  const openMedFor = (animalId) => {
    setMedEditing(null);
    setMedForm({
      livestock_id: animalId, medication_name: "", dosage: "", doses_per_day: 1,
      start_date: todayISO(), days: 7, withdrawal_days: 0, cost: "", vendor: "", notes: "", receipts: [],
    });
    setMedOpen(true);
  };
  const onEditMed = (m) => {
    setMedEditing(m);
    setMedForm({
      livestock_id: m.livestock_id,
      medication_name: m.medication_name,
      dosage: m.dosage || "",
      doses_per_day: m.doses_per_day || 1,
      start_date: m.start_date,
      days: m.days,
      withdrawal_days: m.withdrawal_days || 0,
      cost: m.cost || "",
      vendor: m.vendor || "",
      notes: m.notes || "",
      receipts: m.receipts || [],
    });
    setMedOpen(true);
  };
  const onSubmitMed = async (e) => {
    e.preventDefault();
    const yearFromStart = medForm.start_date ? new Date(medForm.start_date).getFullYear() : year;
    const payload = {
      ...medForm,
      doses_per_day: parseInt(medForm.doses_per_day, 10) || 1,
      days: parseInt(medForm.days, 10) || 1,
      withdrawal_days: parseInt(medForm.withdrawal_days, 10) || 0,
      cost: parseFloat(medForm.cost) || 0,
      year: yearFromStart,
    };
    try {
      if (medEditing) await api.put(`/medications/${medEditing.id}`, payload);
      else            await api.post(`/medications`, payload);
      toast.success(medEditing ? "Medication course updated" : "Medication course added");
      setMedOpen(false); setMedEditing(null);
      load();
    } catch { toast.error("Could not save medication"); }
  };
  const onDeleteMed = async (id) => {
    if (!window.confirm("Delete this medication course? Any linked expense will be removed too.")) return;
    await api.delete(`/medications/${id}`);
    toast.success("Medication course deleted");
    load();
  };

  // Animal CRUD
  const onSubmitAnimal = async (e) => {
    e.preventDefault();
    const yearFromDate = aForm.purchase_date ? new Date(aForm.purchase_date).getFullYear() : year;
    const payload = {
      ...aForm,
      count: parseInt(aForm.count) || 1,
      purchase_cost: parseFloat(aForm.purchase_cost) || 0,
      current_value: parseFloat(aForm.current_value) || 0,
      year: yearFromDate || year,
    };
    try {
      if (aEditing) await api.put(`/livestock/${aEditing.id}`, payload);
      else await api.post("/livestock", payload);
      toast.success(aEditing ? "Animal updated" : "Animal added");
      setAOpen(false); setAEditing(null); setAForm(blankAnimal);
      refreshYears();
      load();
    } catch { toast.error("Could not save"); }
  };
  const onEditAnimal = (a) => {
    setAEditing(a);
    setAForm({
      name: a.name, species: a.species, breed: a.breed || "", kind: a.kind, count: a.count,
      birth_date: a.birth_date || "", purchase_date: a.purchase_date || "", purchase_cost: a.purchase_cost || "",
      current_value: a.current_value || "", current_value_as_of: a.current_value_as_of || "",
      status: a.status, notes: a.notes || "", receipts: a.receipts || [],
    });
    setAOpen(true);
  };
  const onDeleteAnimal = async (id) => {
    if (!window.confirm("Delete this animal/group? All linked care events, sales, and auto-expenses will be removed.")) return;
    await api.delete(`/livestock/${id}`);
    load();
  };

  // Care events
  const openEventModal = (animal, existing = null) => {
    if (existing) {
      setEEditing(existing);
      setEForm({
        livestock_id: existing.livestock_id, date: existing.date, event_type: existing.event_type,
        description: existing.description, cost: existing.cost, vendor: existing.vendor || "",
        notes: existing.notes || "", receipts: existing.receipts || [],
      });
    } else {
      setEEditing(null);
      setEForm({ ...blankEvent, livestock_id: animal.id, date: todayISO() });
    }
    setEOpen(true);
  };
  const onSubmitEvent = async (e) => {
    e.preventDefault();
    const yearFromDate = new Date(eForm.date).getFullYear();
    const payload = { ...eForm, cost: parseFloat(eForm.cost) || 0, year: yearFromDate || year };
    try {
      if (eEditing) await api.put(`/care-events/${eEditing.id}`, payload);
      else await api.post("/care-events", payload);
      toast.success(eEditing ? "Event updated" : "Event logged (expense auto-created)");
      setEOpen(false); setEEditing(null); setEForm(blankEvent);
      refreshYears();
      load();
    } catch { toast.error("Could not save event"); }
  };
  const onDeleteEvent = async (id) => {
    if (!window.confirm("Delete this care event? The linked expense will be removed.")) return;
    await api.delete(`/care-events/${id}`);
    load();
  };

  // Sales
  const openSaleModal = (animal) => {
    setSForm({ ...blankSale, livestock_id: animal.id, date: todayISO() });
    setSOpen(true);
  };
  const onSubmitSale = async (e) => {
    e.preventDefault();
    const yearFromDate = new Date(sForm.date).getFullYear();
    const payload = {
      ...sForm,
      quantity: parseFloat(sForm.quantity) || 0,
      price_per_unit: parseFloat(sForm.price_per_unit) || 0,
      year: yearFromDate || year,
    };
    try {
      await api.post("/livestock-sales", payload);
      toast.success("Sale logged (revenue auto-posted to Yields)");
      setSOpen(false); setSForm(blankSale);
      refreshYears();
      load();
    } catch { toast.error("Could not save sale"); }
  };
  const onDeleteSale = async (id) => {
    if (!window.confirm("Delete this sale? The linked income will be removed.")) return;
    await api.delete(`/livestock-sales/${id}`);
    load();
  };

  const totalAnimals = animals.filter((a) => a.status === "active").reduce((s, a) => s + (a.kind === "group" ? a.count : 1), 0);
  const totalCareCost = events.filter((ev) => ev.year === year).reduce((s, ev) => s + (ev.cost || 0), 0);
  const totalSalesRevenue = sales.filter((s) => s.year === year).reduce((sum, s) => sum + (s.total_revenue || 0), 0);
  const upcomingBirths = breedingStatus.filter((b) => b.status === "imminent" || b.status === "overdue");

  return (
    <div>
      <TopBar
        title="Livestock"
        subtitle="Track animals, vet visits, feed, and sales. Care events auto-post as expenses; sales auto-post as income."
        actions={
          <button
            data-testid="add-livestock-btn"
            onClick={() => { setAEditing(null); setAForm({ ...blankAnimal, purchase_date: todayISO() }); setAOpen(true); }}
            className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#9D391E] transition-colors flex items-center gap-2 mt-6"
          >
            <Plus className="w-4 h-4" /> Add Animal
          </button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-[#1F1E1B] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Active Animals</div>
          <div className="font-display text-3xl font-black tabular">{totalAnimals}</div>
        </div>
        <div className="bg-[#BC4727] text-white border border-[#BC4727] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#F8D5C8]">Care Cost ({year})</div>
          <div className="font-display text-3xl font-black tabular">{fmtUSD(totalCareCost)}</div>
        </div>
        <div className="bg-[#1A4135] text-white border border-[#1A4135] p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#C8D5CE]">Sales Revenue ({year})</div>
          <div className="font-display text-3xl font-black tabular">{fmtUSD(totalSalesRevenue)}</div>
        </div>
      </div>

      {upcomingBirths.length > 0 && (
        <div className="bg-[#FDF5E6] border border-[#E5A835] p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="breeding-alerts">
          <div className="flex items-center gap-2 shrink-0">
            <Baby className="w-5 h-5 text-[#BC4727]" strokeWidth={2.2} />
            <div className="font-display font-bold text-sm uppercase tracking-wider">
              {upcomingBirths.length} expected birth{upcomingBirths.length > 1 ? "s" : ""} soon
            </div>
          </div>
          <div className="flex-1 flex flex-wrap gap-2 text-xs">
            {upcomingBirths.slice(0, 6).map((b) => (
              <span key={b.livestock_id} className={`px-2 py-1 border font-semibold ${b.status === "overdue" ? "bg-[#BC4727] text-white border-[#BC4727]" : "bg-white border-[#E5A835]"}`} data-testid={`birth-alert-${b.livestock_id}`}>
                <Baby className="inline w-3 h-3 mr-1" />
                {b.name} ({b.species}) · due {b.expected_due_date} ({b.status === "overdue" ? `${Math.abs(b.days_until_due)}d late` : `${b.days_until_due}d`})
              </span>
            ))}
            {upcomingBirths.length > 6 && <span className="text-[#66645E] px-2 py-1">+{upcomingBirths.length - 6} more</span>}
          </div>
        </div>
      )}

      {animals.length === 0 ? (
        <EmptyState
          icon={Beef}
          title="No livestock yet"
          hint="Add a single animal (Bessie the milk cow) or a group (50 layer hens). Then log vet visits, feed, and sales."
          testid="empty-livestock"
        />
      ) : (
        <div className="space-y-4">
          {animals.map((a) => {
            const Icon = speciesIcon(a.species);
            const evList = eventsFor(a.id);
            const slList = salesFor(a.id);
            const open = !!expanded[a.id];
            const statusStamp = a.status === "active" ? "stamp-green" : a.status === "sold" ? "" : "stamp-red";
            return (
              <div key={a.id} className="bg-white border border-[#1F1E1B]" data-testid={`animal-card-${a.id}`}>
                <div className="p-4 sm:p-5 flex flex-wrap items-start gap-4">
                  <div className="w-12 h-12 bg-[#1F1E1B] flex items-center justify-center text-white shrink-0">
                    <Icon className="w-6 h-6" strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="stamp">{a.species}</span>
                      {a.kind === "group" ? (
                        <span className="stamp">Group · {a.count}</span>
                      ) : (
                        <span className="stamp">Individual</span>
                      )}
                      <span className={`stamp ${statusStamp}`}>{a.status}</span>
                      {breedingFor(a.id) && (
                        <span
                          className={`stamp ${breedingFor(a.id).status === "overdue" ? "stamp-red" : breedingFor(a.id).status === "imminent" ? "" : "stamp-green"}`}
                          data-testid={`breeding-badge-${a.id}`}
                        >
                          <Baby className="inline w-3 h-3 mr-1" />
                          Due {breedingFor(a.id).expected_due_date}
                          {breedingFor(a.id).status === "overdue"
                            ? ` (${Math.abs(breedingFor(a.id).days_until_due)}d late)`
                            : ` (${breedingFor(a.id).days_until_due}d)`}
                        </span>
                      )}
                      {a.receipts?.length > 0 && (
                        <ReceiptBadge receipts={a.receipts} onClick={() => setGalleryReceipts(a.receipts)} testid={`livestock-receipts-${a.id}`} />
                      )}
                    </div>
                    <h3 className="font-display text-xl font-bold mt-2">{a.name}</h3>
                    <div className="text-xs text-[#66645E] mt-1 flex flex-wrap gap-x-4 gap-y-1">
                      {a.breed && <span>{a.breed}</span>}
                      {a.birth_date && <span>Born {a.birth_date}</span>}
                      {a.purchase_date && <span>Purchased {a.purchase_date}</span>}
                    </div>
                    {a.notes && <div className="text-sm text-[#66645E] mt-2">{a.notes}</div>}
                  </div>
                  <div className="text-right">
                    {a.purchase_cost > 0 && (
                      <>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#66645E]">Purchase</div>
                        <div className="font-display text-xl font-black tabular">{fmtUSD(a.purchase_cost)}</div>
                      </>
                    )}
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#66645E] mt-2">Care total</div>
                    <div className="font-display text-lg font-black tabular text-[#BC4727]">{fmtUSD(careCostFor(a.id))}</div>
                    {salesRevenueFor(a.id) > 0 && (
                      <>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#66645E] mt-2">Sales</div>
                        <div className="font-display text-lg font-black tabular text-[#1A4135]">{fmtUSD(salesRevenueFor(a.id))}</div>
                      </>
                    )}
                  </div>
                </div>

                <div className="border-t border-[#E5E2D6] px-4 sm:px-5 py-2.5 flex items-center justify-between bg-[#FAF9F4] flex-wrap gap-2">
                  <button
                    data-testid={`toggle-livestock-${a.id}`}
                    onClick={() => setExpanded({ ...expanded, [a.id]: !open })}
                    className="text-xs font-bold uppercase tracking-wider flex items-center gap-1 text-[#1F1E1B]"
                  >
                    {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                    Care ({evList.length}) · Meds ({medsFor(a.id).length}) · Sales ({slList.length})
                  </button>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid={`add-care-${a.id}`}
                      onClick={() => openEventModal(a)}
                      className="bg-white border border-[#1F1E1B] px-3 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-[#1F1E1B] hover:text-white"
                    >
                      <Stethoscope className="w-3 h-3" /> Log Care
                    </button>
                    <button
                      data-testid={`add-med-${a.id}`}
                      onClick={() => openMedFor(a.id)}
                      className="bg-[#5B2C82] text-white px-3 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-[#4A2469]"
                    >
                      <Stethoscope className="w-3 h-3" /> Med
                    </button>
                    <button
                      data-testid={`add-sale-${a.id}`}
                      onClick={() => openSaleModal(a)}
                      className="bg-[#1A4135] text-white px-3 py-1 text-xs font-bold uppercase tracking-wider flex items-center gap-1 hover:bg-[#133128]"
                    >
                      <DollarSign className="w-3 h-3" /> Sale
                    </button>
                    <button data-testid={`edit-livestock-${a.id}`} onClick={() => onEditAnimal(a)} className="p-1 px-2 border border-[#1F1E1B] text-xs hover:bg-[#1F1E1B] hover:text-white">
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button data-testid={`delete-livestock-${a.id}`} onClick={() => onDeleteAnimal(a.id)} className="p-1 px-2 border border-[#BC4727] text-[#BC4727] text-xs hover:bg-[#BC4727] hover:text-white">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>

                {open && (
                  <div className="border-t border-[#E5E2D6]" data-testid={`details-${a.id}`}>
                    {evList.length > 0 && (
                      <div className="overflow-x-auto">
                        <div className="px-4 sm:px-5 pt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Care Events</div>
                        <table className="ledger-table">
                          <thead>
                            <tr><th>Date</th><th>Type</th><th>Description</th><th>Vendor</th><th className="num">Cost</th><th>Receipts</th><th style={{ width: 90 }}></th></tr>
                          </thead>
                          <tbody>
                            {evList.map((ev) => {
                              const ET = EVENT_TYPES.find((t) => t.value === ev.event_type);
                              return (
                                <tr key={ev.id} data-testid={`care-row-${ev.id}`}>
                                  <td className="tabular">{ev.date}</td>
                                  <td><span className="stamp">{ET?.label || ev.event_type}</span></td>
                                  <td className="font-semibold">{ev.description}</td>
                                  <td className="text-[#66645E]">{ev.vendor || "—"}</td>
                                  <td className="num font-bold">{fmtUSD(ev.cost)}</td>
                                  <td><ReceiptBadge receipts={ev.receipts} onClick={() => setGalleryReceipts(ev.receipts)} testid={`care-receipts-${ev.id}`} /></td>
                                  <td>
                                    <div className="flex gap-1.5 justify-end">
                                      <button data-testid={`edit-care-${ev.id}`} onClick={() => openEventModal(a, ev)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                                      <button data-testid={`delete-care-${ev.id}`} onClick={() => onDeleteEvent(ev.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {medsFor(a.id).length > 0 && (
                      <div className="overflow-x-auto border-t border-[#E5E2D6]" data-testid={`meds-section-${a.id}`}>
                        <div className="px-4 sm:px-5 pt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Medications</div>
                        <table className="ledger-table">
                          <thead>
                            <tr><th>Start</th><th>Medication</th><th>Dosage</th><th className="num">Days</th><th>Course</th><th>Withdrawal</th><th className="num">Cost</th><th style={{ width: 90 }}></th></tr>
                          </thead>
                          <tbody>
                            {medsFor(a.id).map((m) => {
                              const today = todayISO();
                              let status, statusColor;
                              if (today < m.start_date)             { status = "scheduled";  statusColor = "#66645E"; }
                              else if (today <= (m.end_date || "")) { status = "in progress"; statusColor = "#5B2C82"; }
                              else if (m.withdrawal_clear_date && today < m.withdrawal_clear_date)
                                                                    { status = "withdrawal"; statusColor = "#C77E1A"; }
                              else                                  { status = "complete";   statusColor = "#1A4135"; }
                              return (
                                <tr key={m.id} data-testid={`med-row-${m.id}`}>
                                  <td className="tabular">{m.start_date}</td>
                                  <td className="font-semibold">{m.medication_name}</td>
                                  <td className="text-[#66645E]">{m.dosage || "—"}{m.doses_per_day > 1 ? ` × ${m.doses_per_day}/d` : ""}</td>
                                  <td className="num">{m.days}</td>
                                  <td>
                                    <div className="text-xs tabular">{m.start_date} → {m.end_date || "—"}</div>
                                    <span className="inline-block text-[9px] uppercase tracking-wider px-1.5 py-0.5 text-white" style={{ background: statusColor }} data-testid={`med-status-${m.id}`}>{status}</span>
                                  </td>
                                  <td className="text-[#66645E]">
                                    {m.withdrawal_days > 0
                                      ? <div className="text-xs">+{m.withdrawal_days}d → <span className="tabular">{m.withdrawal_clear_date}</span></div>
                                      : "—"}
                                  </td>
                                  <td className="num font-bold">{m.cost > 0 ? fmtUSD(m.cost) : "—"}</td>
                                  <td>
                                    <div className="flex gap-1.5 justify-end">
                                      <button data-testid={`edit-med-${m.id}`} onClick={() => onEditMed(m)} className="p-1.5 border border-[#1F1E1B] hover:bg-[#1F1E1B] hover:text-white"><Pencil className="w-3 h-3" /></button>
                                      <button data-testid={`delete-med-${m.id}`} onClick={() => onDeleteMed(m.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {slList.length > 0 && (
                      <div className="overflow-x-auto border-t border-[#E5E2D6]">
                        <div className="px-4 sm:px-5 pt-3 text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Sales</div>
                        <table className="ledger-table">
                          <thead>
                            <tr><th>Date</th><th>Type</th><th>Product</th><th className="num">Qty</th><th className="num">Price</th><th className="num">Revenue</th><th>Buyer</th><th style={{ width: 60 }}></th></tr>
                          </thead>
                          <tbody>
                            {slList.map((s) => (
                              <tr key={s.id} data-testid={`sale-row-${s.id}`}>
                                <td className="tabular">{s.date}</td>
                                <td><span className="stamp">{s.sale_type}</span></td>
                                <td>{s.product}</td>
                                <td className="num">{fmtNum(s.quantity)} {s.unit}</td>
                                <td className="num">{fmtUSD(s.price_per_unit)}</td>
                                <td className="num font-bold">{fmtUSD(s.total_revenue)}</td>
                                <td className="text-[#66645E]">{s.buyer || "—"}</td>
                                <td>
                                  <button data-testid={`delete-sale-${s.id}`} onClick={() => onDeleteSale(s.id)} className="p-1.5 border border-[#BC4727] text-[#BC4727] hover:bg-[#BC4727] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {evList.length === 0 && slList.length === 0 && (
                      <div className="p-6 text-center text-sm text-[#66645E]">No care events or sales yet.</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Animal modal */}
      {aOpen && (
        <Modal title={aEditing ? "Edit Animal" : "Add Livestock"} onClose={() => setAOpen(false)}>
          <form onSubmit={onSubmitAnimal} className="space-y-4" data-testid="livestock-form">
            <Field label="Name / Tag" required>
              <input data-testid="livestock-name-input" required value={aForm.name} onChange={(e) => setAForm({ ...aForm, name: e.target.value })} placeholder='Bessie / "50 layer hens" / Tag #142' className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Species" required>
                <AddableSelect
                  domain="species"
                  defaults={SPECIES}
                  value={aForm.species}
                  onChange={(v) => setAForm({ ...aForm, species: v })}
                  testid="livestock-species-select"
                  required
                />
              </Field>
              <Field label="Breed">
                <AddableSelect
                  domain="breed"
                  defaults={[]}
                  value={aForm.breed}
                  onChange={(v) => setAForm({ ...aForm, breed: v })}
                  testid="livestock-breed-input"
                  placeholder="— Select breed —"
                />
              </Field>
            </div>
            <div className="border border-[#1F1E1B] p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-2">Track as</div>
              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" data-testid="kind-individual-radio" checked={aForm.kind === "individual"} onChange={() => setAForm({ ...aForm, kind: "individual", count: 1 })} />
                  Individual
                </label>
                <label className="flex items-center gap-1.5 text-sm cursor-pointer">
                  <input type="radio" data-testid="kind-group-radio" checked={aForm.kind === "group"} onChange={() => setAForm({ ...aForm, kind: "group" })} />
                  Group / herd
                </label>
                {aForm.kind === "group" && (
                  <div className="flex items-center gap-2">
                    <input
                      data-testid="livestock-count-input"
                      type="number" min="1" value={aForm.count}
                      onChange={(e) => setAForm({ ...aForm, count: e.target.value })}
                      className="w-20 bg-white border border-[#1F1E1B] px-2 py-1 text-sm tabular"
                    />
                    <span className="text-xs text-[#66645E]">head/birds</span>
                  </div>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Birth Date">
                <input data-testid="livestock-birth-input" type="date" value={aForm.birth_date} onChange={(e) => setAForm({ ...aForm, birth_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Status">
                <select data-testid="livestock-status-select" value={aForm.status} onChange={(e) => setAForm({ ...aForm, status: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  {STATUS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Purchase Date">
                <input data-testid="livestock-purchase-date-input" type="date" value={aForm.purchase_date} onChange={(e) => setAForm({ ...aForm, purchase_date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Purchase Cost ($)">
                <input data-testid="livestock-purchase-cost-input" type="number" step="0.01" value={aForm.purchase_cost} onChange={(e) => setAForm({ ...aForm, purchase_cost: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            {/* Fair-market / current value — overrides purchase_cost on the balance sheet */}
            <div className="border-2 border-[#1F1E1B] bg-[#F5F4F0] p-3" data-testid="livestock-fmv-section">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] mb-2">
                Current Market Value <span className="text-[9px] text-[#66645E] normal-case tracking-normal font-normal">(optional — overrides purchase price on Balance Sheet)</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Current Value ($)">
                  <input data-testid="livestock-current-value-input" type="number" step="0.01" placeholder="e.g. 1800 if bought a cow for $900" value={aForm.current_value} onChange={(e) => setAForm({ ...aForm, current_value: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
                </Field>
                <Field label="Value As Of">
                  <input data-testid="livestock-current-value-asof-input" type="date" value={aForm.current_value_as_of} onChange={(e) => setAForm({ ...aForm, current_value_as_of: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
                </Field>
              </div>
              {parseFloat(aForm.current_value) > 0 && parseFloat(aForm.purchase_cost) > 0 && (
                <div className="text-[11px] text-[#66645E] mt-1.5">
                  Balance Sheet will show <strong className="tabular text-[#1A4135]">{fmtUSD(parseFloat(aForm.current_value) || 0)}</strong> (FMV). Purchased for <span className="tabular">{fmtUSD(parseFloat(aForm.purchase_cost) || 0)}</span>.
                </div>
              )}
            </div>
            <Field label="Notes">
              <textarea data-testid="livestock-notes-input" value={aForm.notes} onChange={(e) => setAForm({ ...aForm, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <ReceiptUploader receipts={aForm.receipts} onChange={(receipts) => setAForm({ ...aForm, receipts })} testidPrefix="livestock-receipt" />
            {aForm.purchase_cost > 0 && (
              <div className="text-[11px] text-[#66645E] bg-[#FAF7EC] border border-[#E5E2D6] p-2">
                <strong>Heads up:</strong> Purchase cost will auto-create an Expense entry (category: Livestock Purchase) for {fmtUSD(parseFloat(aForm.purchase_cost) || 0)}.
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setAOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="livestock-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Care event modal */}
      {eOpen && (
        <Modal title={eEditing ? "Edit Care Event" : "Log Care Event"} onClose={() => setEOpen(false)}>
          <form onSubmit={onSubmitEvent} className="space-y-4" data-testid="care-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Date" required>
                <input data-testid="care-date-input" type="date" required value={eForm.date} onChange={(e) => setEForm({ ...eForm, date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Type" required>
                <select data-testid="care-type-select" value={eForm.event_type} onChange={(e) => setEForm({ ...eForm, event_type: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  {EVENT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Description" required>
              <input data-testid="care-description-input" required value={eForm.description} onChange={(e) => setEForm({ ...eForm, description: e.target.value })} placeholder="Annual checkup, 50 lb feed, AI session…" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Vendor">
                <input data-testid="care-vendor-input" value={eForm.vendor} onChange={(e) => setEForm({ ...eForm, vendor: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Cost ($)" required>
                <input data-testid="care-cost-input" type="number" step="0.01" required value={eForm.cost} onChange={(e) => setEForm({ ...eForm, cost: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            <Field label="Notes">
              <textarea data-testid="care-notes-input" value={eForm.notes} onChange={(e) => setEForm({ ...eForm, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <ReceiptUploader receipts={eForm.receipts} onChange={(receipts) => setEForm({ ...eForm, receipts })} testidPrefix="care-receipt" />
            <div className="text-[11px] text-[#66645E] bg-[#FAF7EC] border border-[#E5E2D6] p-2">
              <strong>Heads up:</strong> Saving will auto-create an Expense entry. Schedule F category will be Vet & Medicine, Feed, Breeding, or Equipment based on the event type.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setEOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="care-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Event</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Sale modal */}
      {sOpen && (
        <Modal title="Log Livestock Sale" onClose={() => setSOpen(false)}>
          <form onSubmit={onSubmitSale} className="space-y-4" data-testid="sale-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Date" required>
                <input data-testid="sale-date-input" type="date" required value={sForm.date} onChange={(e) => setSForm({ ...sForm, date: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Sale Type" required>
                <select data-testid="sale-type-select" value={sForm.sale_type} onChange={(e) => setSForm({ ...sForm, sale_type: e.target.value, product: e.target.value === "animal" ? "head" : "eggs", unit: e.target.value === "animal" ? "head" : "dozen" })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm">
                  <option value="animal">Animal (live/slaughter)</option>
                  <option value="product">Product (eggs, milk, wool…)</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Product" required>
                <AddableSelect
                  domain="sale_product"
                  defaults={["head", "eggs", "milk", "beef", "calf", "wool", "honey"]}
                  value={sForm.product}
                  onChange={(v) => setSForm({ ...sForm, product: v })}
                  testid="sale-product-input"
                  required
                />
              </Field>
              <Field label="Unit">
                <AddableSelect
                  domain="sale_unit"
                  defaults={["head", "dozen", "gallons", "lbs", "tons", "lb"]}
                  value={sForm.unit}
                  onChange={(v) => setSForm({ ...sForm, unit: v })}
                  testid="sale-unit-input"
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Quantity" required>
                <input data-testid="sale-quantity-input" type="number" step="0.01" required value={sForm.quantity} onChange={(e) => setSForm({ ...sForm, quantity: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Price / Unit" required>
                <input data-testid="sale-price-input" type="number" step="0.01" required value={sForm.price_per_unit} onChange={(e) => setSForm({ ...sForm, price_per_unit: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
              <Field label="Buyer">
                <input data-testid="sale-buyer-input" value={sForm.buyer} onChange={(e) => setSForm({ ...sForm, buyer: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <Field label="Notes">
              <textarea data-testid="sale-notes-input" value={sForm.notes} onChange={(e) => setSForm({ ...sForm, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <ReceiptUploader receipts={sForm.receipts} onChange={(receipts) => setSForm({ ...sForm, receipts })} testidPrefix="sale-receipt" />
            <div className="text-[11px] text-[#66645E] bg-[#FAF7EC] border border-[#E5E2D6] p-2">
              <strong>Heads up:</strong> Saving will post {fmtUSD((parseFloat(sForm.quantity) || 0) * (parseFloat(sForm.price_per_unit) || 0))} of revenue to your Yields page (and Schedule F).
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setSOpen(false)} className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="sale-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Sale</button>
            </div>
          </form>
        </Modal>
      )}

      {medOpen && (
        <Modal title={medEditing ? "Edit Medication Course" : "New Medication Course"} onClose={() => { setMedOpen(false); setMedEditing(null); }}>
          <form onSubmit={onSubmitMed} className="space-y-4" data-testid="medication-form">
            <Field label="Animal / Batch" required>
              <select
                data-testid="med-animal-select"
                required
                value={medForm.livestock_id}
                onChange={(e) => setMedForm({ ...medForm, livestock_id: e.target.value })}
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
              >
                <option value="">Select animal…</option>
                {animals.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.species}{a.kind === "group" ? ` (×${a.count})` : ""}</option>)}
              </select>
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Medication" required>
                <AddableSelect
                  domain="medication"
                  defaults={["Penicillin G", "LA-200 (Oxytetracycline)", "Bovi-Shield", "Banamine", "Dewormer (Ivermectin)", "Vitamin B12"]}
                  value={medForm.medication_name}
                  onChange={(v) => setMedForm({ ...medForm, medication_name: v })}
                  testid="med-name-select"
                  required
                />
              </Field>
              <Field label="Dosage">
                <input
                  data-testid="med-dosage-input"
                  value={medForm.dosage}
                  onChange={(e) => setMedForm({ ...medForm, dosage: e.target.value })}
                  placeholder="e.g. 10cc IM"
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
                />
              </Field>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <Field label="Start Date" required>
                <input
                  data-testid="med-start-date-input"
                  type="date"
                  required
                  value={medForm.start_date}
                  onChange={(e) => setMedForm({ ...medForm, start_date: e.target.value })}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
                />
              </Field>
              <Field label="Days" required>
                <input
                  data-testid="med-days-input"
                  type="number"
                  min="1"
                  step="1"
                  required
                  value={medForm.days}
                  onChange={(e) => setMedForm({ ...medForm, days: e.target.value })}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
                />
              </Field>
              <Field label="Doses / Day">
                <input
                  data-testid="med-doses-per-day-input"
                  type="number"
                  min="1"
                  step="1"
                  value={medForm.doses_per_day}
                  onChange={(e) => setMedForm({ ...medForm, doses_per_day: e.target.value })}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Withdrawal Days (milk/meat)">
                <input
                  data-testid="med-withdrawal-days-input"
                  type="number"
                  min="0"
                  step="1"
                  value={medForm.withdrawal_days}
                  onChange={(e) => setMedForm({ ...medForm, withdrawal_days: e.target.value })}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
                />
                <div className="text-[10px] text-[#66645E] mt-1">Days AFTER last dose before milk/meat is sellable.</div>
              </Field>
              <Field label="Cost (optional)">
                <input
                  data-testid="med-cost-input"
                  type="number"
                  step="0.01"
                  value={medForm.cost}
                  onChange={(e) => setMedForm({ ...medForm, cost: e.target.value })}
                  className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular"
                />
                <div className="text-[10px] text-[#66645E] mt-1">Enter a number and a Farm <em>vet</em> expense is auto-created.</div>
              </Field>
            </div>

            <Field label="Vendor (optional)">
              <input
                data-testid="med-vendor-input"
                value={medForm.vendor}
                onChange={(e) => setMedForm({ ...medForm, vendor: e.target.value })}
                placeholder="Co-op, Tractor Supply, vet, etc."
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
              />
            </Field>

            <Field label="Notes (optional)">
              <textarea
                data-testid="med-notes-input"
                rows={2}
                value={medForm.notes}
                onChange={(e) => setMedForm({ ...medForm, notes: e.target.value })}
                className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm"
              />
            </Field>

            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => { setMedOpen(false); setMedEditing(null); }} data-testid="med-cancel-btn" className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="med-submit-btn" className="bg-[#5B2C82] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#4A2469]">Save Course</button>
            </div>
          </form>
        </Modal>
      )}

      {galleryReceipts && (
        <ReceiptGallery receipts={galleryReceipts} onClose={() => setGalleryReceipts(null)} />
      )}
    </div>
  );
}
